/**
 * Git context for drift items.
 *
 * When `cai check` reports "src/index.ts does not exist", the user wants to know
 * *why* and *when*. This module finds the commit that deleted or renamed a file,
 * so the drift report can say "deleted in abc123 (3 days ago): refactor: split server"
 * instead of just flagging the symptom.
 *
 * All functions are synchronous (execFileSync) and cache per-process to keep
 * `cai check` fast even when many drift items reference the same paths.
 */
import { execFileSync } from "node:child_process";

export interface GitDeletionInfo {
  commit: string;
  message: string;
  ago: string;
  renamedTo?: string;
}

const cache = new Map<string, GitDeletionInfo | null>();

function git(cwd: string, args: string[]): string {
  try {
    return execFileSync("git", args, {
      cwd,
      encoding: "utf8",
      stdio: ["ignore", "pipe", "ignore"],
      maxBuffer: 4 * 1024 * 1024,
    });
  } catch {
    return "";
  }
}

/** Convert "3 days ago" / "5 weeks ago" — git's relative format already does this. */
function parseRelative(rel: string): string {
  return rel.trim();
}

/**
 * Find the commit that deleted a file. Also detects renames via the same commit's
 * --name-status: if the deletion was paired with an add of similar content, git
 * reports it as R (rename).
 *
 * Returns null if the file was never tracked or still exists.
 */
export function findDeletionCommit(projectRoot: string, relPath: string): GitDeletionInfo | null {
  const cacheKey = `${projectRoot}:${relPath}`;
  if (cache.has(cacheKey)) return cache.get(cacheKey) ?? null;

  // Step 1: find the deletion commit
  // Format: <hash>|<subject>|<relative date>
  const log = git(projectRoot, [
    "log",
    "--diff-filter=D",
    "--follow",
    "-1",
    "--format=%H|%s|%ar",
    "--",
    relPath,
  ]);

  if (!log.trim()) {
    cache.set(cacheKey, null);
    return null;
  }

  const [commit, message, ago] = log.trim().split("|");
  if (!commit) {
    cache.set(cacheKey, null);
    return null;
  }

  // Step 2: check if this commit also renamed the file
  // git show --name-status <commit> shows R<score>\told\tnew lines
  const nameStatus = git(projectRoot, ["show", "--name-status", "--format=", commit]);
  let renamedTo: string | undefined;
  for (const line of nameStatus.split("\n")) {
    // Rename lines look like: R100\told/path\tnew/path
    if (line.startsWith("R")) {
      const parts = line.split("\t");
      if (parts.length >= 3 && parts[1] === relPath) {
        renamedTo = parts[2];
        break;
      }
    }
  }

  const info: GitDeletionInfo = {
    commit: commit.slice(0, 7),
    message,
    ago: parseRelative(ago),
    renamedTo,
  };
  cache.set(cacheKey, info);
  return info;
}

/** Format a deletion info as a single human-readable line for drift reports. */
export function formatDeletionInfo(info: GitDeletionInfo): string {
  if (info.renamedTo) {
    return `renamed to ${info.renamedTo} in ${info.commit} (${info.ago}): ${info.message}`;
  }
  return `deleted in ${info.commit} (${info.ago}): ${info.message}`;
}

/** Clear the per-process cache. Used by tests. */
export function clearGitBlameCache(): void {
  cache.clear();
}
